import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, Loader2, ChevronLeft, Globe, Mail, Eye, Plug, Save } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";

const GOLD = "#D97706";

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0"
      style={{ backgroundColor: checked ? GOLD : "#CBD5E1" }}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

const COMS = [
  ["email_novedades", "Novedades del ecosistema", "Anuncios y nuevas funciones de ConstruLink."],
  ["email_ofertas", "Ofertas y promociones", "Descuentos del Marketplace, Software y Academia."],
  ["email_resumen", "Resumen semanal", "Un correo con la actividad de tu cuenta cada semana."],
];
const PRIV = [
  ["mostrar_email", "Mostrar mi correo", "Otros miembros podrán ver tu correo en tu perfil público."],
  ["mostrar_telefono", "Mostrar mi teléfono", "Otros miembros podrán ver tu teléfono en tu perfil público."],
  ["visible_busqueda", "Visible en búsquedas", "Tu perfil aparece en el buscador de ConstruLink Red."],
];

export default function ConfiguracionGeneralPage() {
  const [cfg, setCfg] = useState(undefined);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api.get("/cuenta/preferencias").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put("/cuenta/preferencias", cfg);
      setCfg(data);
      toast.success("Preferencias guardadas.");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setGuardando(false); }
  };

  if (cfg === undefined) return <div className="flex items-center gap-2 text-slate-500 p-16"><Loader2 className="animate-spin" size={16} /> Cargando…</div>;
  if (!cfg) return <p className="p-8 text-sm text-slate-400">No se pudo cargar la configuración.</p>;

  const setCom = (k, v) => setCfg((c) => ({ ...c, comunicaciones: { ...c.comunicaciones, [k]: v } }));
  const setPriv = (k, v) => setCfg((c) => ({ ...c, privacidad: { ...c.privacidad, [k]: v } }));

  return (
    <div className="px-5 md:px-8 py-8 max-w-3xl" data-testid="configuracion-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver al Ecosistema</Link>
      <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3"><Settings size={30} style={{ color: GOLD }} /> Configuración</h1>
      <p className="mt-2 text-slate-600">Preferencias generales, comunicaciones y privacidad de tu cuenta.</p>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="config-general-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Globe size={18} style={{ color: GOLD }} /> General</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Idioma</label>
            <select value={cfg.idioma} onChange={(e) => setCfg((c) => ({ ...c, idioma: e.target.value }))} data-testid="config-idioma" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200">
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Zona horaria</label>
            <select value={cfg.zona_horaria} onChange={(e) => setCfg((c) => ({ ...c, zona_horaria: e.target.value }))} data-testid="config-zona" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200">
              {["America/Lima", "America/Bogota", "America/Mexico_City", "America/Santiago", "America/Argentina/Buenos_Aires"].map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="config-comunicaciones-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Mail size={18} style={{ color: GOLD }} /> Comunicaciones</h2>
        <div className="mt-2 divide-y divide-slate-100">
          {COMS.map(([k, t, d]) => (
            <div key={k} className="flex items-center justify-between gap-4 py-3">
              <div><p className="text-sm font-semibold text-slate-800">{t}</p><p className="text-xs text-slate-500">{d}</p></div>
              <Toggle checked={!!cfg.comunicaciones[k]} onChange={(v) => setCom(k, v)} testid={`config-com-${k}`} />
            </div>
          ))}
        </div>
      </section>

      <section className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="config-privacidad-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Eye size={18} style={{ color: GOLD }} /> Datos públicos y privacidad</h2>
        <div className="mt-2 divide-y divide-slate-100">
          {PRIV.map(([k, t, d]) => (
            <div key={k} className="flex items-center justify-between gap-4 py-3">
              <div><p className="text-sm font-semibold text-slate-800">{t}</p><p className="text-xs text-slate-500">{d}</p></div>
              <Toggle checked={!!cfg.privacidad[k]} onChange={(v) => setPriv(k, v)} testid={`config-priv-${k}`} />
            </div>
          ))}
        </div>
      </section>

      <section className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft p-5 opacity-90" data-testid="config-integraciones-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Plug size={18} style={{ color: GOLD }} /> Integraciones</h2>
        <p className="mt-1 text-sm text-slate-500">Conecta herramientas externas a tu cuenta.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {["Google Calendar", "WhatsApp", "Contabilidad"].map((n) => (
            <span key={n} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-slate-500">{n} · Próximamente</span>
          ))}
        </div>
      </section>

      <div className="mt-6 flex justify-end">
        <button onClick={guardar} disabled={guardando} data-testid="config-guardar" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: GOLD }}>
          {guardando ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Guardar cambios
        </button>
      </div>
    </div>
  );
}
